/**
 * BLRRD color module — math used by the render pipeline + LUT bake.
 * Pure functions, no closure over `E` or DOM. Importable from main thread
 * (via window.BlrrdColor bridge) AND from worker threads (direct ESM import).
 *
 * Soft-clip knee:
 *   - uint8 LUT path (`softClip`): 1280-entry lookup table, k=24px shoulder.
 *     Toe (near 0) is quadratic ease-in. Shoulder (near 255) is quadratic
 *     ease-out. Identity in 24..231. Pre-built once at module load.
 *   - float path (`softClipF`): same shape but in float 0..1 space, k=24/255.
 *     Used in places where Float32 precision matters before quantization.
 *
 * HSL: classic and float-space variants. Both input/output ranges:
 *   - rgbToHsl  : R,G,B 0..255  → H,S,L 0..1
 *   - hslToRgb  : H,S,L 0..1    → R,G,B 0..255
 *   - rgbToHslF : R,G,B 0..1    → H,S,L 0..1
 *   - hslToRgbF : H,S,L 0..1    → R,G,B 0..1
 *
 * NB: kept byte-identical to the prior inline definitions in
 * blrrd-editor-v15.js — verified by the pixel-hash gate.
 */

export const SC_OFF = 512;
export const SC_LUT = new Uint8Array(1280);
(function buildSoftClipLUT() {
    const k = 24;
    for (let i = 0; i < 1280; i++) {
        const v = i - SC_OFF;
        let o;
        if (v >= k && v <= 255 - k) o = v;
        else if (v <= -k) o = 0;
        else if (v >= 255 + k) o = 255;
        else if (v < k) { const t = (v + k) / (2 * k); o = k * t * t; }
        else { const t = (v - (255 - k)) / (2 * k); o = (255 - k) + k * (2 * t - t * t); }
        SC_LUT[i] = Math.max(0, Math.min(255, Math.round(o)));
    }
})();

export function softClip(v) {
    if (v !== v) return 0; /* NaN guard */
    const idx = Math.round(v) + SC_OFF;
    return SC_LUT[idx < 0 ? 0 : idx > 1279 ? 1279 : idx];
}

export function softClipF(v) {
    if (v !== v) return 0;
    if (v <= 0) return 0;
    if (v >= 1) return 1;
    const k = 0.0941; /* 24/255 — matches uint8 knee width */
    if (v >= k && v <= 1 - k) return v;
    if (v < k) { const t = (v + k) / (2 * k); return k * t * t; }
    const t = (v - (1 - k)) / (2 * k);
    return (1 - k) + k * (2 * t - t * t);
}

export function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    let h = 0, s = 0, l = (mx + mn) / 2;
    if (mx !== mn) {
        const d = mx - mn;
        s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (mx === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
    }
    return [h, s, l];
}

export function hslToRgb(h, s, l) {
    if (s === 0) { const v = l * 255; return [v, v, v]; }
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1; if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [hue2rgb(p, q, h + 1 / 3) * 255, hue2rgb(p, q, h) * 255, hue2rgb(p, q, h - 1 / 3) * 255];
}

export function rgbToHslF(r, g, b) {
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    let h = 0, s = 0, l = (mx + mn) / 2;
    if (mx !== mn) {
        const d = mx - mn;
        s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (mx === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
    }
    return [h, s, l];
}

export function hslToRgbF(h, s, l) {
    if (s === 0) return [l, l, l];
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1; if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}
